<script>
export default {
    name: 'EventItemList',

    props: {
        occasion: {
            type: Object,
            required: true
        },
        monthName: {
            type: String,
            required: true
        },
        dayNumber: {
            type: String,
            required: true
        },
        mode: {
            type: String,
            required: false,
            default: () => 'web'
        },
    },

    methods: {
        getMomentDate(isoDate) {
            // eslint-disable-next-line no-undef
            return moment(isoDate)
        }
    }
}
</script>

<template>
    <div>
        <div
            v-if="!occasion.isHighlighted"
            class="eventRow-card d-flex"
        >
            <div class="date date-calendar pr-4 d-flex flex-column justify-content-between">
                <div>
                    <div class="month text-uppercase">
                        {{ monthName }}
                    </div>
                    <div class="day">
                        {{ dayNumber }}
                    </div>
                    <div>
                        {{ dateFormatDayname(getMomentDate(occasion.date)) }}
                    </div>
                    <div class="hour">
                        <svg class="icon icon-hour"><use xlink:href="#icon-hour" /></svg>
                        <span>{{ occasion.time }}</span>
                    </div>
                </div>

                <a
                    v-if="mode === 'web'"
                    :href="occasion.url"
                    target="_blank"
                    class="btn-more text-transform-none d-md-none py-2"
                >
                    {{ __('Details') }}
                </a>
            </div>
            <div class="flex-1 d-flex flex-column justify-content-between">
                <div class="d-flex justify-content-between">
                    <div class="title limit-text-md-1">
                        <a
                            v-if="mode === 'web'"
                            :href="occasion.url"
                            target="_blank"
                        >
                            {{ occasion.name }}
                        </a>

                        <a
                            v-else
                            :href="occasion.kioszkUrl"
                        >
                            {{ occasion.name }}
                        </a>
                    </div>
                    <div>
                        <div v-if="mode === 'web'">
                            <a
                                :href="occasion.button.url"
                                target="_blank"
                                class="d-none d-md-block h-auto"
                                :class="[occasion.button.style, { 'disabled': occasion.button.disabled }]"
                            >
                                {{ occasion.button.label }}
                            </a>
                            <a
                                v-if="occasion.stbutton.url"
                                :href="occasion.stbutton.url"
                                target="_blank"
                                class="d-none d-md-block mt-2 h-auto"
                                :class="[occasion.stbutton.style]"
                            >
                                {{ occasion.stbutton.label }}
                            </a>
                        </div>
                        <div v-else>
                            <a
                                :href="occasion.kioszkUrl"
                                class="d-none d-md-block btn btn-primary mt-2"
                            >
                                {{ __('Details') }}
                            </a>
                        </div>
                    </div>
                </div>

                <div class="d-flex justify-content-between align-items-start align-items-md-end flex-column flex-md-row">
                    <div class="desc flex-md-1">
                        <div class="param">
                            <div
                                v-if="occasion.series"
                                class="mb-2 occasion-series-scoped"
                            >
                                <a
                                    v-if="mode === 'web'"
                                    :href="occasion.series.url"
                                    target="_blank"
                                >
                                    {{ occasion.series.name }}
                                </a>
                                <p v-else class="mb-0 d-inline-block pr-2">
                                    {{ occasion.series.name }}
                                </p>
                            </div>
                            <div class="type">
                                <div v-if="mode === 'web'">
                                    <a
                                        v-for="(category, idx) in occasion.categories"
                                        :key="idx"
                                        :href="category.url"
                                    >
                                        {{ category.name }}
                                    </a>
                                </div>
                                <div v-else>
                                    <p
                                        v-for="(category, idx) in occasion.categories"
                                        :key="idx"
                                        class="mb-0 d-inline-block pr-2"
                                    >
                                        {{ category.name }}
                                    </p>
                                </div>
                            </div>

                            <div
                                v-if="occasion.genres && occasion.genres.length"
                                class="genre"
                            >
                                {{ __('Genre') }}:
                                <span
                                    v-for="(genre, idx) in occasion.genres"
                                    :key="idx"
                                >
                                    <a v-if="mode === 'web'" :href="genre.url">{{ genre.name }}<span v-if="idx < occasion.genres.length-1 && occasion.genres.length > 1">,&nbsp;</span></a>
                                    <p v-else class="mb-0 d-inline-block pr-2">{{ genre.name }}<span v-if="idx < occasion.genres.length-1 && occasion.genres.length > 1">,&nbsp;</span></p>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="d-flex justify-content-md-end w-100 align-items-center justify-content-end pt-3 mt-3 pt-md-0 flex-md-0">
                        <div v-if="mode === 'web'">
                            <a
                                :href="occasion.url"
                                class="btn-more text-transform-none d-none d-md-flex"
                            >
                                {{ __('Details') }} <svg class="icon icon-shortarrow d-none d-md-inline"><use xlink:href="#icon-shortarrow" /></svg>
                            </a>
                        </div>

                        <div v-if="mode === 'web'">
                            <a
                                :href="occasion.button.url"
                                target="_blank"
                                class="d-block d-md-none h-auto"
                                :class="[occasion.button.style, { 'disabled': occasion.button.disabled }]"
                            >
                                {{ occasion.button.label }}
                            </a>
                            <a
                                v-if="occasion.stbutton.url"
                                :href="occasion.stbutton.url"
                                target="_blank"
                                class="d-block d-md-none h-auto"
                                :class="[occasion.stbutton.style]"
                            >
                                {{ occasion.stbutton.label }}
                            </a>
                        </div>
                        <div v-else>
                            <a
                                :href="occasion.kioszkUrl"
                                class="d-block d-md-none btn btn-primary"
                            >
                                {{ __('Details') }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-else
            class="eventRow-card d-flex"
        >
            <div class="date date-calendar pr-4 d-flex flex-column justify-content-between">
                <div>
                    <div class="month text-uppercase">
                        {{ monthName }}
                    </div>
                    <div class="day">
                        {{ dayNumber }}
                    </div>
                    <div>
                        {{ dateFormatDayname(getMomentDate(occasion.date)) }}
                    </div>
                    <div class="hour">
                        <svg class="icon icon-hour"><use xlink:href="#icon-hour" /></svg>
                        <span>{{ occasion.time }}</span>
                    </div>
                </div>

                <a
                    v-if="mode === 'web'"
                    :href="occasion.url"
                    target="_blank"
                    class="btn-more text-transform-none d-md-none py-2"
                >
                    {{ __('Details') }}
                </a>
            </div>
            <div class="flex-1 d-flex align-items-center flex-wrap">
                <!-- BEGIN no highlighted picture on mobile -->
                <div>
                    <div class="occasion-image-container">
                        <a
                            v-if="mode === 'web'"
                            :href="occasion.url"
                            class="cover-right-faded"
                            tabindex="-1"
                            aria-hidden="true"
                        >
                            <img
                                :src="occasion.image"
                                :alt="occasion.name"
                            >
                        </a>
                        <a
                            v-else
                            :href="occasion.kioszkUrl"
                            class="cover-right-faded"
                            tabindex="-1"
                            aria-hidden="true"
                        >
                            <img
                                :src="occasion.image"
                                :alt="occasion.name"
                            >
                        </a>
                    </div>
                </div>
                <!-- END no highlighted picture on mobile -->
                <div class="flex-1 mt-5 mt-md-0 ml-md-3">
                    <div class="d-flex justify-content-between align-items-md-end">
                        <!-- BEGIN limit text to 2 line on desktop-->
                        <div class="d-none d-md-block">
                            <div class="title limit-text-2">
                                <a
                                    v-if="mode === 'web'"
                                    :href="occasion.url"
                                    target="_blank"
                                >
                                    {{ occasion.name }}
                                </a>
                                <a
                                    v-else
                                    :href="occasion.kioszkUrl"
                                >
                                    {{ occasion.name }}
                                </a>
                            </div>
                        </div>
                        <!-- END limit text to 2 line on desktop-->
                        <div class="d-block d-md-none">
                            <div class="title limit-text-md-1">
                                <a
                                    v-if="mode === 'web'"
                                    :href="occasion.url"
                                    target="_blank"
                                >
                                    {{ occasion.name }}
                                </a>
                                <a
                                    v-else
                                    :href="occasion.kioszkUrl"
                                >
                                    {{ occasion.name }}
                                </a>
                            </div>
                        </div>
                        <div v-if="mode === 'web'">
                            <a
                                :href="occasion.button.url"
                                target="_blank"
                                class="d-none d-md-block h-auto"
                                :class="[occasion.button.style, { 'disabled': occasion.button.disabled }]"
                                :tabindex="occasion.button.disabled ? -1 : 0"
                            >
                                {{ occasion.button.label }}
                            </a>
                            <a
                                v-if="occasion.stbutton.url"
                                :href="occasion.stbutton.url"
                                target="_blank"
                                class="d-none d-md-block mt-2 h-auto"
                                :class="[occasion.stbutton.style]"
                            >
                                {{ occasion.stbutton.label }}
                            </a>
                        </div>
                        <div v-else>
                            <a
                                :href="occasion.kioszkUrl"
                                class="d-none d-md-block btn btn-primary"
                            >
                                {{ __('Details') }}
                            </a>
                        </div>
                    </div>

                    <div class="d-flex justify-content-between align-items-start align-items-md-end flex-column flex-md-row">
                        <div class="desc flex-md-1">
                            <div class="param">
                                <div
                                    v-if="occasion.series"
                                    class="mb-2 occasion-series-scoped"
                                >
                                    <a
                                        v-if="mode === 'web'"
                                        :href="occasion.series.url"
                                        target="_blank"
                                    >
                                        {{ occasion.series.name }}
                                    </a>
                                    <p v-else class="mb-0 d-inline-block pr-2">
                                        {{ occasion.series.name }}
                                    </p>
                                </div>
                                <div class="type">
                                    <div v-if="mode === 'web'">
                                        <a
                                            v-for="(category, idx) in occasion.categories"
                                            :key="idx"
                                            :href="category.url"
                                        >
                                            {{ category.name }}
                                        </a>
                                    </div>
                                    <div v-else>
                                        <p v-for="(category, idx)  in occasion.categories"
                                           :key="idx"
                                           class="mb-0 d-inline-block pr-2"
                                        >
                                            {{ category.name }}
                                        </p>
                                    </div>
                                </div>

                                <div
                                    v-if="occasion.genres && occasion.genres.length"
                                    class="genre"
                                >
                                    {{ __('Genre') }}:
                                    <span
                                        v-for="(genre, idx) in occasion.genres"
                                        :key="idx"
                                    >
                                        <a v-if="mode === 'web'" :href="genre.url">{{ genre.name }}<span v-if="idx < occasion.genres.length-1 && occasion.genres.length > 1">,&nbsp;</span></a>
                                        <p v-else class="mb-0 d-inline-block pr-2">{{ genre.name }}<span v-if="idx < occasion.genres.length-1 && occasion.genres.length > 1">,&nbsp;</span></p>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex justify-content-md-end w-100 align-items-center justify-content-end pt-3 mt-3 pt-md-0 flex-md-0">
                            <div v-if="mode === 'web'">
                                <a
                                    :href="occasion.url"
                                    class="btn-more text-transform-none d-none d-md-flex"
                                >
                                    {{ __('Details') }} <svg class="icon icon-shortarrow d-none d-md-inline"><use xlink:href="#icon-shortarrow" /></svg>
                                </a>
                            </div>

                            <div>
                                <div v-if="mode === 'web'">
                                    <a
                                        :href="occasion.button.url"
                                        target="_blank"
                                        class="d-block d-md-none h-auto"
                                        :class="[occasion.button.style, { 'disabled': occasion.button.disabled }]"
                                    >
                                        {{ occasion.button.label }}
                                    </a>
                                    <a
                                        v-if="occasion.stbutton.url"
                                        :href="occasion.stbutton.url"
                                        target="_blank"
                                        class="d-block d-md-none mt-2 h-auto"
                                        :class="[occasion.stbutton.style]"
                                    >
                                        {{ occasion.stbutton.label }}
                                    </a>
                                </div>

                                <div v-else>
                                    <a
                                        :href="occasion.kioszkUrl"
                                        class="d-block d-md-none btn btn-primary"
                                    >
                                        {{ __('Details') }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.occasion-series-scoped {
    margin-top: -6px;
    padding-left: 6px;
    border-left: 6px solid #0068B1;
}
</style>

<style lang="scss">
    .cover-right-faded {
        display: block;
        width: 100%;
        overflow: hidden;
        position: relative;

        img {
            aspect-ratio: 285 / 180;
            width: 100%;
            max-width: 100%;
            object-fit: cover;
        }

        &::after {
            content: "";
            position: absolute;
            right: 0;
            bottom: 0;
            top: 0;
            width: 50px;
            background: linear-gradient(-90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
        }
    }

    .occasion-image-container {
        width: 285px;
        height: 180px;
    }

    @media (max-width: 768px) {
        .occasion-image-container {
            width: 100%;
        }
    }
</style>
